import React, { FC, useEffect } from 'react';
import PaymentMethod from './Method';
import { generateID } from '~reducers';
import {
  useDispatch,
  useSplitPayment,
  useSelector,
  useLogger,
  useStaffCan,
  useI18n,
} from '~Hooks';
import * as actions from '~actions';
import { PAYMENT_STATUS } from '~../__queries__/globalTypes';
import { useStorage } from '~Components/Storage';
import { SplitPaymentMethodWrapper } from '~Scenes/Tables/Components/PayWith/SplitPayment';

interface Props {
  orderId: string;
  total: number;
  isRefunding: boolean;
  setIsRefunding: (isRefunding: boolean) => void;
  onComplete: () => void;
  onClose: () => void;
  standardPay: (appliedDiscount: any, method: string) => void;
  appliedDiscount: any;
  refundPayment: () => void;
  disabled: boolean;
}

const PaymentMethods: FC<Props> = ({
  orderId,
  total,
  isRefunding,
  setIsRefunding,
  onComplete,
  standardPay,
  appliedDiscount,
  refundPayment,
  disabled,
  onClose,
}) => {
  const { i18n } = useI18n();
  const dispatch = useDispatch();
  const staffCan = useStaffCan();
  const [storage] = useStorage();
  const logger = useLogger('payment');

  const order = useSelector((state) => state.POSOrders.find((o) => o.id === orderId))!;
  const POSPayment = useSelector((state) => state.POSPayments.find((o) => o.order === orderId))!;
  const paymentMethods = useSelector((state) =>
    state.POSPaymentMethods.sort((a, b) => a.title.localeCompare(b.title)),
  );

  const splitPayment = useSplitPayment(orderId);

  const amountRemaining = total - splitPayment.amountPaid;

  /**
   * Split finished logic
   */
  useEffect(() => {
    // If amountRemaining is not 0 the payments are not done
    if (!splitPayment.splitOrder || amountRemaining > 0 || isRefunding) {
      return;
    }

    // Sums up the tips in all subpayments
    const totalTip = splitPayment.splitOrder?.payments.reduce((acc, payment) => {
      const posPayment = splitPayment.orderPOSPayments.find((p) => p.id === payment.id);

      return acc + (posPayment?.meta?.tip || 0);
    }, 0);

    const orderForSync = {
      amount: splitPayment.amountPaid,
      payment: {
        ...POSPayment,
        meta: {
          ...POSPayment.meta,
          tip: totalTip,
        },
      },
      order: {
        ...order,
        subPayments: splitPayment.splitOrder.payments.map((payment) => {
          const posPayment = splitPayment.orderPOSPayments.find((p) => p.id === payment.id);
          const tip = posPayment?.meta.tip;

          return {
            transactionId: payment.meta?.transactionId ?? payment.id,
            type: payment.method,
            status: PAYMENT_STATUS.SUCCESS,
            amount: payment.amount,
            tip,
          };
        }),
      },
    };
    dispatch({
      type: actions.SYNC_POS_ORDER,
      ...orderForSync,
    });

    dispatch({
      type: actions.FINISH_SPLIT_POS_ORDER,
    });

    dispatch({
      type: actions.SEND_TABLE_ITEMS,
      order,
      items: order.items,
      origin: order.origin,
    });

    dispatch({
      type: actions.PRINT_RECEIPT,
      isInvoicePayment:
        ['PRINTED_INVOICE', 'INVOICE'].includes(POSPayment.method) ||
        (splitPayment.splitOrder.payments &&
          splitPayment.splitOrder.payments.find((p) =>
            ['PRINTED_INVOICE', 'INVOICE'].includes(p.method),
          )),
      id: orderId,
      order,
    });

    setTimeout(() => {
      dispatch({
        type: actions.CLEAR_SPLIT_POS_ORDER,
      });
      onComplete();
      onClose();
    }, 500);
  }, [amountRemaining]);

  /**
   * Remove payment if it fails
   */
  useEffect(() => {
    if (splitPayment.latestPOSPayment?.status !== 'FAILED' || isRefunding) {
      return;
    }
    dispatch({
      type: actions.REMOVE_PAYMENT_FROM_SPLIT_ORDER,
      payment: splitPayment.latestPOSPayment?.id,
    });
  }, [splitPayment.latestPOSPayment, isRefunding]);

  /**
   * Refunding flow logic
   */
  useEffect(() => {
    // If we are not refunding
    if (!isRefunding) {
      return;
    }

    // If we are refunding and all payments have been refunded we are done
    if (isRefunding && !splitPayment.nextRefundPayment) {
      setIsRefunding(false);
      dispatch({
        type: actions.CLEAR_SPLIT_POS_ORDER,
      });
    }

    refundPayment();
  }, [isRefunding, splitPayment.nextRefundPayment?.id]);

  const startSplitPayment = (amount: number, method: string, methodTitle: string) => {
    logger.info(`QSR: Creating split payment. Method: ${method}. Amount: ${amount}.`, {
      method,
      amount,
    });
    // Don't allow payment if amount is bigger than the amount remaining
    if (amount > amountRemaining || isRefunding) {
      return;
    }

    // If this is the first payment of the split
    if (!splitPayment.splitOrder) {
      // If the full amount is paid in the first payment, treat it as a standard payment
      if (splitPayment.amountPaid === 0 && amount >= total) {
        onClose();
        return standardPay(appliedDiscount, method);
      }

      // Sets up split order
      standardPay(appliedDiscount, method);
      dispatch({
        type: actions.CREATE_SPLIT_POS_ORDER,
        order: orderId,
      });
    }

    const paymentId = generateID();

    dispatch({
      type: actions.ADD_PAYMENT_TO_SPLIT_ORDER,
      payment: {
        id: paymentId,
        method,
        methodTitle,
        amount,
        meta: {},
      },
    });

    const variant = method === 'ADYEN' ? storage.adyenTerminals[0].POIID : undefined;

    dispatch(
      actions.initiatePayment(orderId, {
        id: paymentId,
        method,
        variant,
        amount,
        canCancel: true,
      }),
    );
  };

  const filteredPaymentMethods = paymentMethods
    .filter((method) => method.id !== 'INVOICE')
    .filter((method) => staffCan.canUsePaymentMethod(method.id));

  return (
    <SplitPaymentMethodWrapper>
      {...filteredPaymentMethods.map((method) => (
        <PaymentMethod
          disabled={disabled}
          title={method.title}
          numberInputTitle={i18n('SplitPayment.SelectAmount')}
          method={method.id}
          amountRemaining={amountRemaining}
          onStartPay={startSplitPayment}
          key={method.id}
        />
      ))}
    </SplitPaymentMethodWrapper>
  );
};

export default PaymentMethods;
